// What a filed email is called, and what it says about itself, once it is in a project.
//
// Pure, because both are decisions: the file name is what a colleague sees in the
// project's document list, and the metadata is what a later version reads to learn
// where threads have gone before.

/** Characters Windows refuses in a file name, plus control characters. */
// eslint-disable-next-line no-control-regex
const ILLEGAL = /[<>:"/\\|?*\u0000-\u001f]/g;
const MAX_NAME_CHARS = 120;

const pad = (n: number) => String(n).padStart(2, "0");

/** yyyy-mm-dd in the user's own time zone, which is the day they saw the message arrive. */
export function isoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Text made safe as a file name: what a file system would refuse becomes a space, and the
 * trailing dots and spaces Windows silently strips are dropped (they would make two names
 * differ only in Jylo). Empty when nothing usable is left.
 */
export function cleanFileName(text: string): string {
  return text
    .replace(ILLEGAL, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_NAME_CHARS)
    .replace(/[. ]+$/, "");
}

/**
 * `2026-09-28 - Re Share purchase agreement.eml`.
 *
 * Date first, so a folder of filed emails sorts into the order they were received. The
 * subject is cleaned of what a file system would refuse, and of the trailing dots and
 * spaces Windows silently strips (which would make two names differ only in Jylo).
 */
export function emailFileName(subject: string, received: Date | undefined): string {
  const title = cleanFileName(subject) || "Email";
  const valid = received && !Number.isNaN(received.getTime());
  return valid ? `${isoDate(received)} - ${title}.eml` : `${title}.eml`;
}

/** The domain of an address, lower-cased; empty when there is none to read. */
export function senderDomain(address: string): string {
  const at = address.lastIndexOf("@");
  if (at < 0) return "";
  return address
    .slice(at + 1)
    .replace(/>.*$/, "")
    .trim()
    .toLowerCase();
}

export interface FiledEmailIdentity {
  conversationId: string;
  internetMessageId: string;
  senderAddress: string;
}

/**
 * Stamped on the document as its `metadataJson`, so that where emails were filed is
 * recorded in Jylo from the first filing on. Nothing reads it yet: it is there so a
 * later "this thread went to X before" has history to answer from, rather than
 * starting empty on the day it ships. Empty values are left out rather than sent as "".
 */
export function filedEmailMetadata(identity: FiledEmailIdentity): Record<string, string> {
  const entries: Array<[string, string]> = [
    ["source", "outlook-addin"],
    ["outlookConversationId", identity.conversationId],
    ["internetMessageId", identity.internetMessageId],
    ["senderDomain", senderDomain(identity.senderAddress)],
  ];
  return Object.fromEntries(entries.filter(([, value]) => value));
}
